/**
 * useReservationTypes — TanStack Query hook over
 * `GET /organizations/{slug}/reservation-types` (SCRUM-334).
 *
 * The booking slice's reservation-type CATALOG hook. It feeds the
 * ReservationsGrid's type filter the FULL active blueprint catalog
 * (`displayName`s — never raw `internalKey`s in the UI) plus the FE's
 * "All types" default and an "Untyped" bucket for `reservationTypeKey ===
 * null` rows.
 *
 * The shape MIRRORS `useReservationsList` exactly — the same closed
 * status union (loading / ready / forbidden / error), the same `FORBIDDEN`
 * sentinel idiom, the same `useCurrentOrg()` slug resolution (RLS-scoped
 * per CLAUDE.md non-negotiable §1), and `retry: false`. A 403 means the
 * caller lost `booking.reservations.read` since `/auth/me` cached, so the
 * type filter renders a permission-denied affordance (no Retry) rather
 * than a broken option list.
 *
 * On `ready` it returns the catalog array (`ReservationTypeDto[]`)
 * straight from the envelope — no projection, no mapping (passthrough;
 * the DTO type is carried by the generated contract, never redefined
 * locally per FE rule §4).
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import type { ReservationTypeDto } from './types.js';
import { bookingQueryKeys } from './queryKeys.js';

/**
 * Internal sentinel for the 403 branch — same idiom as
 * `use-reservations-list.ts`. The queryFn returns this symbol on 403 so
 * we can distinguish forbidden from a generic error in the status union.
 */
const FORBIDDEN = Symbol('reservation-types:forbidden');

export type UseReservationTypesResult =
  | {
      readonly status: 'loading';
      readonly data?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'ready';
      /** Active catalog straight from the envelope — no projection. */
      readonly data: readonly ReservationTypeDto[];
      readonly refetch: () => void;
    }
  | {
      readonly status: 'forbidden';
      readonly data?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'error';
      readonly data?: undefined;
      readonly refetch: () => void;
    };

export function useReservationTypes(): UseReservationTypesResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    queryKey: [...bookingQueryKeys.types(), orgSlug] as const,
    enabled: orgSlug !== null,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET(
        '/organizations/{slug}/reservation-types',
        {
          params: { path: { slug: orgSlug as string } },
        },
      );
      if (result.response.status === 403) {
        return FORBIDDEN;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/reservation-types failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (orgSlug === null || query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) {
    return { status: 'error', refetch };
  }
  if (query.data === FORBIDDEN) {
    return { status: 'forbidden', refetch };
  }

  const envelope = query.data;
  return {
    status: 'ready',
    data: envelope.data,
    refetch,
  };
}
